
/**
 * Module dependencies.
 */

import { NavbarTheme } from 'src/types/navbar';
import { color } from 'src/styles/utils';
import { css } from 'styled-components';
import { switchProp } from 'styled-tools';

/**
 * Export `NavThemeProps` type.
 */

export type NavThemeProps = {
  colorTheme?: NavbarTheme
};

/**
 * Export `borderOpacity`.
 *
 * `Opacity / Neutral Darkest 15` in the design system.
 */

export const borderOpacity = 0.15;

/**
 * Export `overlayBorderOpacity`.
 *
 * `Opacity / White 20` in the design system.
 */

export const overlayBorderOpacity = 0.2;

/**
 * Export `navTransition`.
 *
 * Every layer that reacts to the navbar theme — backgrounds, borders, icon
 * colors, the hero gradient and the mega menu panel — shares one duration and
 * one curve. With different timings they visibly converge at different speeds
 * and the navbar reads as stuttering.
 */

export const navDuration = '0.28s';
export const navTransition = `${navDuration} cubic-bezier(0.4, 0, 0.2, 1)`;

/**
 * Export `setNavColor`.
 *
 * `dark` renders the navbar contents in black, over a solid background.
 * `light` renders them in white, over the hero image.
 */

export const setNavColor = css<NavThemeProps>`
  ${switchProp('colorTheme', {
    dark: css`
      color: ${color('black')};
    `,
    light: css`
      color: ${color('white')};
    `
  })}
`;

/**
 * Export `setNavBorderColor`.
 */

export const setNavBorderColor = css<NavThemeProps>`
  ${switchProp('colorTheme', {
    dark: css`
      border-color: ${color.transparentize('neutralDarkest', borderOpacity)};
    `,
    light: css`
      border-color: ${color.transparentize('white', overlayBorderOpacity)};
    `
  })}
`;

/**
 * Export `navLinkHover`.
 *
 * Shared by the secondary and tertiary nav links.
 */

export const navLinkHover = css`
  &:focus-visible,
  &:hover {
    color: ${color('brown400')};
    text-decoration: underline;
  }
`;
